import {buttonProgress} from "../lib/buttonProgress";
import {fetchJson, toastFetchError} from "../lib/fetch";
import {escapeHTML} from "../lib/escapeHTML";
import {createHtmlElement, toggleHidden, toggleReloadingFade} from "../lib/domFunctions";
import {Modal} from "bootstrap";
import {ProcessListItem} from "../dto/com.rico.sb2.service.process";
import {emptyPage, Page} from "../dto/org.springframework.data.domain";
import {Messages} from "../messages/Messages";
import {coalesce} from "../lib/coalesce";

const messages = new Messages();
const H = escapeHTML;
const TOTAL_SEARCH_LIMIT = 100;
const RESULT_TABLE_MAX_HEIGHT = '50vh';

export type ProcessInputFieldChange = (item: ProcessListItem | null) => void | Promise<boolean>
export type ProcessInputFieldSearch = (search: string, limit: number) => Promise<Page<ProcessListItem>>

export interface ProcessInputModalSearchOptions {
	title?: string
	search?: ProcessInputFieldSearch
}

export class ProcessInputModalSearch {
	private readonly receiver: ProcessInputFieldChange
	private readonly request: (search: string, limit: number) => Promise<Page<ProcessListItem>>
	private readonly modal: Modal

	private readonly searchField: HTMLInputElement;
	private readonly searchEmpty: Element;
	private readonly searchTable: Element;
	private readonly searchNotice: HTMLElement;
	private readonly searchError: HTMLElement;

	private readonly queryTimeout = 300
	private readonly queryMinLetters = 1

	private lastQueryNumber = 0
	private lastQueryText = ''
	private lastQueryResult: any[] = []

	constructor(receiver: ProcessInputFieldChange, options: ProcessInputModalSearchOptions = {}) {
		this.receiver = receiver
		this.request = coalesce(options.search, (search, limit) => fetchJson(`/processes/listPage?search=${encodeURIComponent(search)}&offset=0&limit=${limit}`))
		const title = coalesce(options.title, messages.get('ProcessInputField.searchTitle'))

		const content = createHtmlElement('div', {'tab-index': '-1', class: 'modal modal-lg'}, `
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header border-bottom-0">
        <h5 class="modal-title" id="exampleModalLabel">${escapeHTML(title)}</h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body pt-0">
        <input type="text" class="form-control" data-bind="searchField" placeholder="${escapeHTML(messages.get('ProcessInputField.searchPlaceholder'))}">
        
        <div class="text-muted py-5 text-center hidden" data-bind="searchEmpty">
            ${escapeHTML(messages.get('ProcessInputField.searchEmpty'))}
        </div>
        <div class="text-danger py-5 text-center hidden" data-bind="searchError">
            ${escapeHTML(messages.get('ProcessInputField.searchError'))}
        </div>
        
        <div class="mt-3 mb-0" style="max-height: ${RESULT_TABLE_MAX_HEIGHT};overflow: auto;">
			<table class="table table-borderless table-hover" data-bind="searchTable">
				<thead class="bg-body" style="position: sticky; top:0;">
					<tr>
						<th class="text-nowrap">${escapeHTML(messages.get('ProcessInputField.table.code'))}</th>
						<th class="text-nowrap w-100">${escapeHTML(messages.get('ProcessInputField.table.name'))}</th>
						<th></th>
					</tr>
				</thead>
				<tbody></tbody>        
			</table>
        </div>
        <div class="hidden mt-3 text-muted text-center" data-bind="searchNotice"></div>
      </div>
    </div>
  </div>
`)

		this.searchField = content.querySelector('input[data-bind=searchField]')!!
		this.searchEmpty = content.querySelector('div[data-bind=searchEmpty]')!!
		this.searchError = content.querySelector('div[data-bind=searchError]')!!
		this.searchTable = content.querySelector('table[data-bind=searchTable]')!!
		this.searchNotice = content.querySelector('div[data-bind=searchNotice]')!!

		this.searchField.addEventListener('input', () => this.scheduleQuery());
		this.searchTable.addEventListener('click', (e) => this.onTableClick(e));

		this.modal = new Modal(content)
		this.modal.show();

		this.scheduleQuery(true)
	}

	private get searchTableContainer() {
		return this.searchTable.parentElement!!;
	}

	private scheduleQuery(force: boolean = false) {
		const searchText = this.searchField.value.trim();
		if (!force && (searchText.length < this.queryMinLetters || searchText === this.lastQueryText)) {
			return;
		}

		toggleReloadingFade(this.searchTableContainer, true)
		toggleReloadingFade(this.searchEmpty, true)
		toggleReloadingFade(this.searchError, true)

		this.lastQueryText = searchText
		const queryNumber = ++this.lastQueryNumber
		setTimeout(() => this.executeQuery(queryNumber, searchText), this.queryTimeout);
	}

	private executeQuery(queryNumber: number, searchText: string) {
		if (queryNumber != this.lastQueryNumber) return;

		this.request(searchText, TOTAL_SEARCH_LIMIT)
			.then(json => this.setContent(json, this.searchEmpty))
			.catch(error => {
				toastFetchError(error)
				this.setContent(emptyPage(), this.searchError)
			});
	}

	private setContent(page: Page<ProcessListItem>, emptyNotice: Element) {
		toggleReloadingFade(this.searchTableContainer, false)
		toggleReloadingFade(this.searchEmpty, false)
		toggleReloadingFade(this.searchError, false)

		this.lastQueryResult = page.content;

		const body = this.searchTable.querySelector('tbody')!!
		body.innerHTML = ``;
		toggleHidden(this.searchNotice, true);

		if (page.totalElements == 0) {
			toggleHidden(this.searchTableContainer, true);
			toggleHidden(emptyNotice, false);
			return
		}


		let bodyHtml = ''
		page.content.forEach(item => {
			const buttons = `<button class="btn btn-sm btn-secondary" data-bind="select">${messages.get('button.select')}</button>`;
			bodyHtml += `
            <tr class="align-baseline" data-item="${H(JSON.stringify(item))}">
                <td class="text-nowrap">${H(item.code)}</td>            
                <td>${H(item.name)}</td>            
                <td class="text-nowrap">${buttons}</td>            
            </tr>`
		})

		body.innerHTML = bodyHtml
		this.searchTableContainer.scrollTop = 0;

		toggleHidden(this.searchTableContainer, false);
		toggleHidden(this.searchEmpty, true);
		toggleHidden(this.searchError, true);

		if (page.totalElements > page.numberOfElements) {
			this.searchNotice.innerText = messages.get('ProcessInputField.countNotice', page.numberOfElements, page.totalElements)
			toggleHidden(this.searchNotice, false);
		}
	}

	private onTableClick(e: Event) {
		const target = e.target as HTMLElement
		if (target.tagName === 'BUTTON' && target.getAttribute('data-bind') === 'select') {
			const row = target.closest('tr')!

			const item = JSON.parse(row.getAttribute('data-item')!)

			const detailItem = this.lastQueryResult.filter(a => a.id === item.id)[0]
			if (!detailItem) return

			const receiveProgress = buttonProgress(target)
			const receiveResult = this.receiver(item)
			let receivePromise: Promise<boolean>;
			if (typeof receiveResult === 'undefined') {
				receivePromise = Promise.resolve(true)
			} else {
				receivePromise = Promise.resolve(receiveResult)
			}

			receivePromise.then(success => {
				receiveProgress.stop()
				if (success) {
					this.modal.hide()
				}
			})
		}
	}
}